import { apiClient } from "@/api/api-client";
import courtBranchService from "@/features/court-detail/api/services/court-branches-service";
import { useAuth } from "@/hooks/use-auth";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import Combobox from "../core/forms/combobox";

type Item = {
	month_name: string;
	total_hearings: number;
	pretrial_count: number;
	preliminary_count: number;
	trial_proper_count: number;
	post_judgment_count: number;
};

const HearingsPerMonthChart = ({ hasBranch = false }: { hasBranch: boolean }) => {
	const { user } = useAuth();
	const [data, setData] = useState<Array<Item> | []>([]);
	const [years, setYears] = useState<Array<number> | []>([]);
	const [selectedYear, setSelectedYear] = useState<string>();
	const [selectedBranch, setSelectedBranch] = useState();
	const [meta, setMeta] = useState<any>(null);
	const currentYear = moment().year();
	const fetchYearList = async () => {
		const response = await apiClient.get("/case-year-list");

		const data = response.data.map((item: number) => ({
			value: item,
			label: item,
		}));

		if (response && response.data) {
			setYears(data);
		}
	};

	const loadOptions = async () => {
		const items = await apiClient.get("/case-year-list");

		return items.data.map((item: number) => ({
			value: item,
			label: item,
		}));
	};

	const loadBranchesOptions = async (value: string) => {
		const items = await courtBranchService.list({
			"filter[search]": value,
		});

		return items.data.map((item) => ({
			label: item.formatted_name,
			value: item.id,
		}));
	};

	const fetchStatistics = async (year: string, branchId: string | null = null) => {
		const id = branchId ? branchId : user?.branches?.[0]?.assignable?.id;
		const response = await apiClient.get(`/backoffice/hearings-per-month?year=${year}&branch_id=${id}`);

		if (response && response.data) {
			setData(response.data.data);
			setMeta(response.data.meta);
		}
	};

	const ParentTable = () => {
		return (
			<div className="flex flex-col w-full relative overflow-x-auto">
				<table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
					<thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
						<tr>
							<th className="px-6 py-3">Month</th>
							<th className="px-6 py-3 text-right">Preliminary</th>
							<th className="px-6 py-3 text-right">Pre-Trial</th>
							<th className="px-6 py-3 text-right">Trial Proper</th>
							<th className="px-6 py-3 text-right">Post Judgment</th>
							<th className="px-6 py-3 text-right">Total Hearings</th>
						</tr>
					</thead>
					<tbody>
						{data && data.length
							? data.map((item, index) => (
									<tr key={`month-${index}`} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200">
										<td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{item?.month_name}</td>
										<td className="px-6 py-4 text-right">{item.preliminary_count}</td>
										<td className="px-6 py-4 text-right">{item.pretrial_count}</td>
										<td className="px-6 py-4 text-right">{item.trial_proper_count}</td>
										<td className="px-6 py-4 text-right">{item.post_judgment_count}</td>
										<td className="px-6 py-4 text-right">{item.total_hearings}</td>
									</tr>
								))
							: null}
					</tbody>
					<tfoot>
						<tr className="font-semibold text-gray-900 dark:text-white">
							<th scope="row" className="px-6 py-3 text-base">
								Total
							</th>
							<th scope="row" className="px-6 py-3 text-base text-right">
								{meta && meta.total_preliminary ? meta.total_preliminary : 0}
							</th>
							<th scope="row" className="px-6 py-3 text-base text-right">
								{meta && meta.total_pretrial ? meta.total_pretrial : 0}
							</th>
							<th scope="row" className="px-6 py-3 text-base text-right">
								{meta && meta.total_trial ? meta.total_trial : 0}
							</th>
							<th scope="row" className="px-6 py-3 text-base text-right">
								{meta && meta.total_post ? meta.total_post : 0}
							</th>
							<th scope="row" className="px-6 py-3 text-base text-right">
								{meta && meta.total ? meta.total : 0}
							</th>
						</tr>
					</tfoot>
				</table>
			</div>
		);
	};

	useEffect(() => {
		fetchYearList();
		fetchStatistics(currentYear.toString());
		setSelectedYear(currentYear.toString());
		console.log(user);
	}, []);

	return (
		<div className="grid grid-cols-12 w-full gap-4">
			<div className="col-span-12">
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full h-auto rounded-md border border-border/50 overflow-hidden">
						<div className="flex flex-row w-full p-3 border-b border-border/50 items-center">
							<div className="flex flex-col w-6/12 text-base font-semibold text-foreground-dark">Hearings Per Month</div>
							<div className="flex flex-col w-4/12 text-base font-semibold text-foreground-dark mr-2">
								{years.length && (
									<Combobox
										onValueChange={(value) => {
											console.log("branch", value);
											setSelectedBranch(value as string);
											fetchStatistics(selectedYear as string, value as string);
										}}
										clearable={false}
										placeholderValue={{
											label: user?.branches?.[0]?.assignable?.formatted_name,
											value: user?.branches?.[0]?.assignable?.id,
										}}
										disabled={hasBranch}
										defaultValue={user?.branches?.[0]?.assignable?.id}
										loadOptions={loadBranchesOptions}
									/>
								)}
							</div>
							<div className="flex flex-col w-2/12 text-base font-semibold text-foreground-dark">
								{years.length && (
									<Combobox
										onValueChange={(value) => {
											setSelectedYear(value as string);
											fetchStatistics(value as string, selectedBranch);
										}}
										clearable={false}
										placeholderValue={{
											label: currentYear,
											value: currentYear,
										}}
										defaultValue={currentYear}
										loadOptions={loadOptions}
									/>
								)}
							</div>
						</div>
						<ParentTable />
					</div>
				</div>
			</div>
		</div>
	);
};

export default HearingsPerMonthChart;
